import { cva } from 'class-variance-authority';

import {
  buttonHeightVariants,
  borderRadiusVariants,
  widthVariants,
  transitionVariants,
  typographyPatterns,
  opacityVariants,
  cursorVariants,
  focusRingClasses,
} from '@/lib/shared-variants';

/**
 * Button Group container styles
 */
export const buttonGroupVariants = cva('inline-flex', {
  variants: {
    orientation: {
      vertical: 'flex-col',
      horizontal: 'flex-row',
    },
  },
  defaultVariants: {
    orientation: 'vertical',
  },
});

/**
 * Button Group item styles
 */
export const buttonGroupItemVariants = cva(
  [
    'flex items-center justify-center',
    typographyPatterns.buttonText,
    transitionVariants.easeInOut,
    focusRingClasses,
    `disabled:${opacityVariants[40]} disabled:${cursorVariants.notAllowed}`,
    'relative',
  ],
  {
    variants: {
      appearance: {
        solid: '',
        outline: 'bg-transparent border',
      },
      kind: {
        neutral: '',
        brand: '',
        danger: '',
        inverse: '',
      },
      scale: {
        s: `${buttonHeightVariants.s} ${widthVariants[8]}`,
        m: `${buttonHeightVariants.m} ${widthVariants[12]}`,
        l: `${buttonHeightVariants.l} ${widthVariants[16]}`,
      },
      position: {
        first: '',
        middle: '',
        last: '',
        only: '',
      },
      orientation: {
        vertical: '',
        horizontal: '',
      },
      selected: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // Solid neutral
      {
        appearance: 'solid',
        kind: 'neutral',
        selected: false,
        className: 'bg-neutral-50 text-neutral-900 hover:bg-neutral-100 active:bg-neutral-200',
      },
      {
        appearance: 'solid',
        kind: 'neutral',
        selected: true,
        className: 'bg-neutral-200 text-neutral-900',
      },
      // Solid brand
      {
        appearance: 'solid',
        kind: 'brand',
        selected: false,
        className:
          'bg-brand-primary text-neutral-0 hover:bg-brand-primary-hover active:bg-brand-primary-active',
      },
      {
        appearance: 'solid',
        kind: 'brand',
        selected: true,
        className: 'bg-brand-primary-active text-neutral-0',
      },
      // Solid danger
      {
        appearance: 'solid',
        kind: 'danger',
        selected: false,
        className:
          'bg-status-danger-primary text-neutral-0 hover:bg-status-danger-primary-hover active:bg-status-danger-primary-active',
      },
      {
        appearance: 'solid',
        kind: 'danger',
        selected: true,
        className: 'bg-status-danger-primary-active text-neutral-0',
      },
      // Solid inverse
      {
        appearance: 'solid',
        kind: 'inverse',
        selected: false,
        className: 'bg-neutral-0 text-neutral-900 hover:bg-neutral-50 active:bg-neutral-100',
      },
      {
        appearance: 'solid',
        kind: 'inverse',
        selected: true,
        className: 'bg-neutral-100 text-neutral-900',
      },
      // Outline neutral
      {
        appearance: 'outline',
        kind: 'neutral',
        selected: false,
        className: 'border-neutral-300 text-neutral-900 hover:bg-neutral-50 active:bg-neutral-100',
      },
      {
        appearance: 'outline',
        kind: 'neutral',
        selected: true,
        className: 'border-neutral-300 bg-neutral-100 text-neutral-900',
      },
      // Outline brand
      {
        appearance: 'outline',
        kind: 'brand',
        selected: false,
        className:
          'border-brand-primary text-brand-primary hover:bg-brand-primary/10 active:bg-brand-primary/20',
      },
      {
        appearance: 'outline',
        kind: 'brand',
        selected: true,
        className: 'border-brand-primary bg-brand-primary/20 text-brand-primary',
      },
      // Outline danger
      {
        appearance: 'outline',
        kind: 'danger',
        selected: false,
        className:
          'border-status-danger-primary text-status-danger-primary hover:bg-status-danger-primary/10 active:bg-status-danger-primary/20',
      },
      {
        appearance: 'outline',
        kind: 'danger',
        selected: true,
        className:
          'border-status-danger-primary bg-status-danger-primary/20 text-status-danger-primary',
      },
      // Outline inverse
      {
        appearance: 'outline',
        kind: 'inverse',
        selected: false,
        className: 'border-neutral-0 text-neutral-0 hover:bg-neutral-0/10 active:bg-neutral-0/20',
      },
      {
        appearance: 'outline',
        kind: 'inverse',
        selected: true,
        className: 'border-neutral-0 bg-neutral-0/20 text-neutral-0',
      },

      // Vertical positioning - first item
      {
        orientation: 'vertical',
        position: 'first',
        className: `${borderRadiusVariants.md} rounded-b-none`,
      },
      // Vertical positioning - middle items
      {
        orientation: 'vertical',
        position: 'middle',
        className: 'rounded-none',
      },
      // Vertical positioning - last item
      {
        orientation: 'vertical',
        position: 'last',
        className: `${borderRadiusVariants.md} rounded-t-none`,
      },
      // Vertical positioning - only item
      {
        orientation: 'vertical',
        position: 'only',
        className: borderRadiusVariants.md,
      },

      // Horizontal positioning - first item
      {
        orientation: 'horizontal',
        position: 'first',
        className: `${borderRadiusVariants.md} rounded-r-none`,
      },
      // Horizontal positioning - middle items
      {
        orientation: 'horizontal',
        position: 'middle',
        className: 'rounded-none',
      },
      // Horizontal positioning - last item
      {
        orientation: 'horizontal',
        position: 'last',
        className: `${borderRadiusVariants.md} rounded-l-none`,
      },
      // Horizontal positioning - only item
      {
        orientation: 'horizontal',
        position: 'only',
        className: borderRadiusVariants.md,
      },

      // Vertical borders - not first (prevents double borders)
      {
        orientation: 'vertical',
        position: ['middle', 'last'],
        appearance: 'solid',
        className: 'border-t border-t-black/10',
      },
      {
        orientation: 'vertical',
        position: ['middle', 'last'],
        appearance: 'outline',
        className: '-mt-px',
      },

      // Horizontal borders - not first (prevents double borders)
      {
        orientation: 'horizontal',
        position: ['middle', 'last'],
        appearance: 'solid',
        className: 'border-l border-l-black/10',
      },
      {
        orientation: 'horizontal',
        position: ['middle', 'last'],
        appearance: 'outline',
        className: '-ml-px',
      },
    ],
    defaultVariants: {
      appearance: 'solid',
      kind: 'neutral',
      scale: 'm',
      position: 'middle',
      orientation: 'vertical',
      selected: false,
    },
  }
);
